'use client';
import { useEffect, useState, useRef } from 'react';
import { motion, useInView, animate, useMotionValue, useTransform } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRouter } from 'next/navigation';
import Globe, { INFRASTRUCTURE_MARKERS } from './Globe';

// Animated counter component
function AnimatedCounter({ value, duration = 2 }: { value: number; duration?: number }) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;

    const controls = animate(0, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplayValue(Math.round(v)),
    });

    return () => controls.stop();
  }, [isInView, value, duration]);

  return <span ref={ref}>{displayValue.toLocaleString('en-GB')}</span>;
}

// Floating impact card component
function ImpactCard({
  label,
  value,
  icon: Icon,
  delay,
  position,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  delay: number;
  position: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute ${position} z-20`}
    >
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: delay * 2 }}
        className="px-4 py-3 rounded-2xl bg-white/90 backdrop-blur-md border border-[hsl(var(--border))] shadow-xl shadow-black/5"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(var(--amber))] to-[hsl(var(--amber-light))] flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-xs text-[hsl(var(--slate-medium))] font-medium">{label}</div>
            <div className="text-lg font-bold text-[hsl(var(--slate-deep))] font-mono">{value}</div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Orbiting connection line
function ConnectionArc({ delay }: { delay: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, pathLength: 0 }}
      animate={{ opacity: [0, 0.5, 0], pathLength: 1 }}
      transition={{ delay, duration: 3, repeat: Infinity, repeatDelay: 2 }}
      className="absolute inset-0 pointer-events-none"
    >
      <svg className="w-full h-full" viewBox="0 0 100 100">
        <motion.path
          d="M 20 50 Q 50 20 80 50"
          fill="none"
          stroke="hsl(var(--amber))"
          strokeWidth="0.3"
          strokeDasharray="2 2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: [0, 1, 1], opacity: [0, 1, 0] }}
          transition={{ delay, duration: 3, repeat: Infinity, repeatDelay: 2 }}
        />
      </svg>
    </motion.div>
  );
}


export default function Hero() {
  const router = useRouter();
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true });
  const [activeMarker, setActiveMarker] = useState<typeof INFRASTRUCTURE_MARKERS[0] | null>(null);

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-[hsl(var(--background))] via-[hsl(var(--background))] to-[hsl(var(--amber))]/[0.03]"
    >
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--slate-dark)) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--slate-dark)) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Gradient orbs */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-[hsl(var(--amber))]/10 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[hsl(var(--teal))]/5 to-transparent rounded-full blur-3xl" />

      {/* Main content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-0">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center min-h-screen lg:min-h-0">
          {/* Left side - Content */}
          <div className="order-2 lg:order-1 text-center lg:text-left">

            {/* Main headline */}
            <div className="overflow-hidden mb-2">
              <motion.h1
                initial={{ y: 100, opacity: 0 }}
                animate={isInView ? { y: 0, opacity: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-[hsl(var(--slate-deep))]"
              >
                Global Infrastructure.
              </motion.h1>
            </div>
            <div className="overflow-hidden mb-6">
              <motion.h1
                initial={{ y: 100, opacity: 0 }}
                animate={isInView ? { y: 0, opacity: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-serif font-medium leading-[1.1] tracking-tight"
              >
                <span className="text-gradient-amber">Real Costs.</span>
              </motion.h1>
            </div>

            {/* The big impact number */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-6"
            >
              <div className="inline-flex items-baseline gap-2">
                <span className="text-[hsl(var(--amber))] text-3xl sm:text-4xl lg:text-5xl font-medium">£</span>
                <span className="font-mono text-5xl sm:text-6xl lg:text-7xl font-bold text-[hsl(var(--slate-deep))] tracking-tighter">
                  <AnimatedCounter value={2847500} duration={2.5} />
                </span>
              </div>
              <div className="text-lg text-[hsl(var(--slate-medium))] mt-2">
                average daily exposure across monitored facilities
              </div>
            </motion.div>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-lg lg:text-xl text-[hsl(var(--slate-medium))] max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed"
            >
              Transform qualitative risk assessments into{' '}
              <span className="text-[hsl(var(--slate-dark))] font-medium">
                precise financial figures
              </span>{' '}
              your board can act on.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-10"
            >
              <Button
                size="lg"
                onClick={() => router.push('/register')}
                className="w-full sm:w-auto bg-[hsl(var(--slate-deep))] hover:bg-[hsl(var(--slate-dark))] text-white shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1 group px-8 h-14 text-base font-semibold rounded-xl"
              >
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-[hsl(var(--slate-medium))]"
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[hsl(var(--teal))]" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>SOC 2 Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[hsl(var(--teal))]" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>150+ Facilities</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[hsl(var(--teal))]" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Enterprise Ready</span>
              </div>
            </motion.div>
          </div>

          {/* Right side - Globe */}
          <div className="order-1 lg:order-2 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-[500px] lg:max-w-none mx-auto"
            >
              {/* The Globe */}
              <Globe className="w-full" />

              {/* Active marker tooltip */}
              {activeMarker && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute bottom-8 left-1/2 -translate-x-1/2 px-4 py-2 bg-[hsl(var(--slate-deep))] text-white rounded-lg text-sm shadow-xl"
                >
                  <div className="font-medium">{activeMarker.label}</div>
                  <div className="text-[hsl(var(--amber))] font-mono">{activeMarker.impact}</div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:block"
      >
        <motion.div
          className="w-6 h-10 rounded-full border-2 border-[hsl(var(--slate-dark))]/20 flex items-start justify-center p-2"
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            className="w-1 h-2 rounded-full bg-[hsl(var(--amber))]"
            animate={{ y: [0, 8, 0], opacity: [1, 0.5, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
}
